"use client";

import React, { useMemo, useState } from "react";
import { useLocale } from "next-intl";
import { FAQ_ENTRIES } from "../help/faq";
import type { HelpAction } from "../help/faq";
import { getFaqById, searchFaq } from "../help/search";

function QuickChipIcon({ id }: { id: string }) {
  const cls = "h-4 w-4 shrink-0";
  const common = {
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    fill: "none",
  };

  switch (id) {
    case "font":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <path {...common} d="M6 20 12 4l6 16M8 15h8" />
        </svg>
      );
    case "images":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <rect {...common} x="3" y="5" width="18" height="14" rx="2" />
          <circle {...common} cx="9" cy="10" r="1.5" />
          <path {...common} d="m21 16-5-5-6 6-3-3-4 4" />
        </svg>
      );
    case "templates":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <rect {...common} x="3" y="4" width="8" height="7" rx="1.5" />
          <rect {...common} x="13" y="4" width="8" height="7" rx="1.5" />
          <rect {...common} x="3" y="13" width="8" height="7" rx="1.5" />
          <rect {...common} x="13" y="13" width="8" height="7" rx="1.5" />
        </svg>
      );
    case "clipart":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <rect {...common} x="4" y="5" width="16" height="14" rx="2" />
          <path {...common} d="M8 15.5 11 12l2.2 2.4 3-4.4L20 15.5" />
          <circle {...common} cx="8.8" cy="9.2" r="1.4" />
        </svg>
      );
    case "background":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <path {...common} d="m5 12.5 6.7-6.7a2 2 0 0 1 2.8 0l3.7 3.7a2 2 0 0 1 0 2.8L12 18.5a2.2 2.2 0 0 1-3.1 0L5 14.6a1.5 1.5 0 0 1 0-2.1Z" />
          <path {...common} d="M8.2 9.3h8.1M4.8 13.4h14.4" />
        </svg>
      );
    case "bleed":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <rect {...common} x="4" y="4" width="16" height="16" rx="1.5" strokeDasharray="3 3" />
          <rect {...common} x="7" y="7" width="10" height="10" rx="1" />
        </svg>
      );
    case "spine":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <path {...common} d="M7 4h10a2 2 0 0 1 2 2v14H9a2 2 0 0 1-2-2V4Z" />
          <path {...common} d="M10 4v16M13 8v8" />
        </svg>
      );
    case "casewrap":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <path {...common} d="M5 5h14v14H5z" />
          <path {...common} d="M8 5v14M16 5v14M11 8h2M11 12h2M11 16h2" />
        </svg>
      );
    case "share":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <circle {...common} cx="18" cy="5" r="3" />
          <circle {...common} cx="6" cy="12" r="3" />
          <circle {...common} cx="18" cy="19" r="3" />
          <path {...common} d="M8.7 10.8 15.3 6.2M8.7 13.2l6.6 4.6" />
        </svg>
      );
    case "guide":
      return (
        <svg viewBox="0 0 24 24" className={cls} aria-hidden="true">
          <path {...common} d="M12 3 19 6.5v7L12 21l-7-7.5v-7L12 3Z" />
          <path {...common} d="M12 7v5l3 2" />
        </svg>
      );
    default:
      return null;
  }
}

export default function HelpChatModal(props: {
  isOpen: boolean;
  onClose: () => void;
  onAction?: (action: HelpAction) => void;
  onStartGuide?: () => void;
}) {
  const { isOpen, onClose } = props;
  const locale = useLocale();
  const lang = locale === "pl" ? "pl" : "en";
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const results = useMemo(() => {
    if (!query.trim()) return FAQ_ENTRIES;
    return searchFaq(query, locale);
  }, [query, locale]);
  const selected =
    (selectedId ? getFaqById(selectedId) : null) ||
    (results.length > 0 ? results[0] : null);
  const quickChips: Array<{
    id: string;
    label: string;
    query: string;
    action?: HelpAction;
  }> = [
    {
      id: "font",
      label: lang === "pl" ? "Tekst" : "Text",
      action: { type: "open_tool", tool: "text" } as const,
      query: lang === "pl" ? "tekst czcionka lista" : "text font list",
    },
    {
      id: "images",
      label: lang === "pl" ? "Zdjęcia" : "Images",
      action: { type: "open_tool", tool: "image" } as const,
      query: lang === "pl" ? "dodawanie zdjęcia" : "insert image",
    },
    {
      id: "clipart",
      label: "Clipart",
      action: { type: "open_tool", tool: "clipart" } as const,
      query: "clipart",
    },
    {
      id: "background",
      label: lang === "pl" ? "Tło" : "Background",
      action: { type: "open_tool", tool: "background" } as const,
      query: lang === "pl" ? "tło wzór" : "background pattern",
    },
    {
      id: "templates",
      label: lang === "pl" ? "Szablony" : "Templates",
      action: { type: "open_templates", tab: "spreads" } as const,
      query: lang === "pl" ? "szablony rozkładówki miniatury" : "templates spreads thumbnails",
    },
    {
      id: "bleed",
      label: lang === "pl" ? "Spad" : "Bleed",
      query: lang === "pl" ? "spad linia cięcia" : "bleed trim line",
    },
    {
      id: "spine",
      label: lang === "pl" ? "Grzbiet" : "Spine",
      query: lang === "pl" ? "tytuł na grzbiecie" : "spine title",
    },
    {
      id: "casewrap",
      label: "Hardcover",
      query: lang === "pl" ? "hardcover cover wrap zawijka" : "hardcover cover wrap casewrap",
    },
    {
      id: "share",
      label: lang === "pl" ? "Udostępnij" : "Share",
      action: { type: "open_share" } as const,
      query: lang === "pl" ? "udostępnienie projektu" : "share project",
    },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[11000]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="fixed right-0 top-[72px] z-[11010] h-[calc(100vh-72px)] w-[430px] max-w-[92vw] overflow-hidden border-l border-t-[3px] border-white/10 bg-[#0f141d] shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div className="text-sm font-semibold uppercase tracking-wide text-white">
            {lang === "pl" ? "Pomoc" : "Help"}
          </div>
          <button
            type="button"
            className="rounded-md p-1 text-white/85 transition-colors hover:bg-white/5 hover:text-white"
            onClick={onClose}
            aria-label={lang === "pl" ? "Zamknij" : "Close"}
            title={lang === "pl" ? "Zamknij" : "Close"}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="border-b border-white/10 p-3">
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedId(null);
            }}
            className="w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm text-white outline-none"
            placeholder={lang === "pl" ? "Np. Jak powiększyć czcionkę?" : "E.g. How do I increase font size?"}
            autoFocus
          />
          <div className="mt-3 flex flex-wrap gap-2">
            {props.onStartGuide ? (
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white hover:bg-white/10"
                onClick={() => {
                  props.onStartGuide?.();
                  onClose();
                }}
              >
                <QuickChipIcon id="guide" />
                <span>{lang === "pl" ? "Przewodnik" : "Guide"}</span>
              </button>
            ) : null}
            {quickChips.map((chip) => (
              <button
                key={chip.id}
                type="button"
                className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white hover:bg-white/10"
                onClick={() => {
                  setQuery(chip.query);
                  setSelectedId(null);
                  if (chip.action && props.onAction) {
                    props.onAction(chip.action);
                    onClose();
                  }
                }}
              >
                <QuickChipIcon id={chip.id} />
                <span>{chip.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="grid h-[calc(100%-118px)] grid-cols-[160px_1fr] overflow-hidden">
          <div className="overflow-auto border-r border-white/10 p-2">
            <div className="space-y-1">
              {results.map((entry) => {
                const active = selected?.id === entry.id;
                return (
                  <button
                    key={entry.id}
                    type="button"
                    className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                      active ? "bg-white/10 text-white" : "text-white/80 hover:bg-white/5"
                    }`}
                    onClick={() => setSelectedId(entry.id)}
                  >
                    {entry.title[lang]}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="overflow-auto p-4">
            {selected ? (
              <div className="space-y-4">
                <div>
                  <div className="text-lg font-semibold text-white">{selected.title[lang]}</div>
                  <div className="mt-2 whitespace-pre-wrap text-sm leading-6 text-white/85">
                    {selected.answer[lang]}
                  </div>
                  {selected.action && props.onAction ? (
                    <button
                      type="button"
                      className="mt-4 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white hover:bg-white/10"
                      onClick={() => {
                        props.onAction?.(selected.action!);
                        onClose();
                      }}
                    >
                      {selected.actionLabel?.[lang] || (lang === "pl" ? "Pokaż gdzie" : "Show me")}
                    </button>
                  ) : null}
                </div>
                {selected.related?.length ? (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-white/50">
                      {lang === "pl" ? "Powiązane tematy" : "Related topics"}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selected.related
                        .map((id) => getFaqById(id))
                        .filter((entry) => entry !== null)
                        .map((rel) => (
                          <button
                            key={rel.id}
                            type="button"
                            className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white hover:bg-white/10"
                            onClick={() => setSelectedId(rel.id)}
                          >
                            {rel.title[lang]}
                          </button>
                        ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="text-sm text-white/70">
                {lang === "pl" ? "Brak wyników." : "No results."}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
